import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FullLoader } from "@/components/cast/Common";
import Layout from "@/components/cast/Layout";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Shore from "@/pages/Shore";
import Boat from "@/pages/Boat";
import Timeline from "@/pages/Timeline";
import Series from "@/pages/Series";
import Episode from "@/pages/Episode";
import DeepWater from "@/pages/DeepWater";
import Module from "@/pages/Module";
import CastNet from "@/pages/CastNet";
import Profile from "@/pages/Profile";
import Groups from "@/pages/Groups";
import Group from "@/pages/Group";
import Parent from "@/pages/Parent";
import Admin from "@/pages/Admin";
import Rules from "@/pages/Rules";
import { KidsHome, KidsEpisode, KidsStickers, KidsMemory } from "@/pages/Kids";
import Plans from "@/pages/Plans";
import JoinInvite from "@/pages/JoinInvite";
import MyVerses from "@/pages/MyVerses";
import Legal from "@/pages/Legal";
import DeepDive from "@/pages/DeepDive";
import DeepNotes from "@/pages/DeepNotes";
import ResetPassword, { ForgotPassword } from "@/pages/ResetPassword";
import PassPage from "@/pages/PassPage";
import CastPlus from "@/pages/CastPlus";

function Protected({ children, needOnboard = true }: { children: any; needOnboard?: boolean }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <FullLoader />;
  if (!user) {
    if (loc.pathname.startsWith("/join/")) localStorage.setItem("cast_return", loc.pathname);
    return <Navigate to="/login" replace />;
  }
  if (needOnboard && !user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const { kid, user } = useAuth();
  if (location.pathname === "/auth/google") return <AuthCallback />;
  if (kid && user === null) return <FullLoader />;
  if (kid && user) {
    return (
      <Routes>
        <Route path="/kids" element={<KidsHome />} />
        <Route path="/kids/episode/:episodeId" element={<KidsEpisode />} />
        <Route path="/kids/stickers" element={<KidsStickers />} />
        <Route path="/kids/memory" element={<KidsMemory />} />
        <Route path="*" element={<Navigate to="/kids" replace />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/privacy" element={<Legal doc="privacy" />} />
      <Route path="/pass/:code" element={<PassPage />} />
      <Route path="/terms" element={<Legal doc="terms" />} />
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/register" element={<AuthPage mode="register" />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<Protected needOnboard={false}><Onboarding /></Protected>} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/shore" element={<Shore />} />
        <Route path="/boat" element={<Boat />} />
        <Route path="/timeline" element={<Timeline />} />
        <Route path="/boat/:seriesId" element={<Series />} />
        <Route path="/episode/:episodeId" element={<Episode />} />
        <Route path="/deep-water" element={<DeepWater />} />
        <Route path="/plans" element={<Plans />} />
        <Route path="/plans/:pid" element={<Plans />} />
        <Route path="/deep-dive" element={<DeepDive />} />
        <Route path="/deep-dive/notes" element={<DeepNotes />} />
        <Route path="/deep-dive/:book/:ch" element={<DeepDive />} />
        <Route path="/plus" element={<CastPlus />} />
        <Route path="/deep-water/:moduleId" element={<Module />} />
        <Route path="/cast-net" element={<CastNet />} />
        <Route path="/u/:username" element={<Profile />} />
        <Route path="/groups" element={<Groups />} />
        <Route path="/groups/:groupId" element={<Group />} />
        <Route path="/parent" element={<Parent />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/rules" element={<Rules />} />
        <Route path="/verses" element={<MyVerses />} />
        <Route path="/join/:token" element={<JoinInvite />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
